import { useState, useEffect, useMemo } from 'react';
import type { Document, DocumentCategory } from '../../graphql/types/admin_dashboard';
import {
  useDocumentCategories,
  useDocumentsForUser,
  useCreateDocument,
  useUpdateDocument,
  useDeleteDocument,
  useCreateDocumentCategory,
  useUpdateDocumentCategory,
  useDeleteDocumentCategory,
} from '../../graphql/hooks/useAdminDashboard';
import { Modal, Button, ConfirmModal } from '../core';
import { SearchableSelect } from '../core/SearchableSelect';
import { MoreVertical, ChevronRight } from 'lucide-react';
import { useAuth } from '../../graphql/hooks/useAuth';
import { DEFAULT_ENDPOINT_URL_EXPORT } from '../../graphql/client';
import toast from 'react-hot-toast';

interface User {
  id: string;
  full_name: string;
}

type ActiveView = 'documents' | 'categories';

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '–';
  const [year, month, day] = dateStr.substring(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Europe/Berlin',
  });
}

const isExpired = (valid_until: string | null) => {
  if (!valid_until) return false;
  return new Date(valid_until) < new Date();
}

const isExpiringSoon = (valid_until: string | null) => {
  if (!valid_until) return false;
  const diff = new Date(valid_until).getTime() - Date.now();
  return diff > 0 && diff < 60 * 24 * 60 * 60 * 1000;
}

const StatusBadge = ({ valid_until }: { valid_until: string | null }) => {
  if (!valid_until) {
    return (
      <span className="inline-flex items-center gap-1 px-4 py-2 rounded-full text-[16px] font-medium bg-[#e8f5e9] text-[#2e7d32]">
        Kein Ablaufdatum
      </span>
    );
  }
  if (isExpired(valid_until)) {
    return (
      <span className="inline-flex items-center gap-1 px-4 py-2 rounded-full text-[16px] font-medium bg-[#fdecea] text-[#c62828]">
        Abgelaufen
      </span>
    );
  }
  if (isExpiringSoon(valid_until)) {
    return (
      <span className="inline-flex items-center gap-1 px-4 py-2 rounded-full text-[16px] font-medium bg-[#fff8e1] text-[#f57f17]">
        Läuft bald ab
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-4 py-2 rounded-full text-[16px] font-medium bg-[#e8f5e9] text-[#2e7d32]">
      Gültig
    </span>
  );
}

interface CategoryModalProps {
  mode: 'create' | 'edit';
  existing?: DocumentCategory;
  onClose: () => void;
  onSuccess: () => void;
}

const CategoryModal = ({ mode, existing, onClose, onSuccess }: CategoryModalProps) => {
  const [name, setName] = useState(existing?.name ?? '');
  const [hasExpiry, setHasExpiry] = useState(existing?.has_expiry ?? false);
  const [validityMonths, setValidityMonths] = useState<string>(
    existing?.validity_months != null ? String(existing.validity_months) : ''
  );
  const [error, setError] = useState('');

  const { mutate: createCategory, isPending: creating } = useCreateDocumentCategory();
  const { mutate: updateCategory, isPending: updating } = useUpdateDocumentCategory();

  const loading = creating || updating;

  const handleSubmit = () => {
    setError('');

    if (!name.trim()) {
      setError('Name ist erforderlich.');
      return;
    }

    const variables = {
      name: name.trim(),
      has_expiry: hasExpiry,
      validity_months: hasExpiry && validityMonths ? Number(validityMonths) : null,
    };

    if (mode === 'create') {
      createCategory(variables, {
        onSuccess: () => { onSuccess(); onClose(); },
        onError: (e: any) => setError(e.message ?? 'Fehler beim Speichern.'),
      });
    } else {
      updateCategory(
        { id: existing!.id, ...variables },
        {
          onSuccess: () => { onSuccess(); onClose(); },
          onError: (e: any) => setError(e.message ?? 'Fehler beim Speichern.'),
        }
      );
    }
  }

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={mode === 'create' ? 'Kategorie erstellen' : 'Kategorie bearbeiten'}
    >
      <div className="space-y-4">
        <div>
          <label className="block text-[12px] font-medium text-[#6b6b6b] mb-1">Name *</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="z. B. Führerschein"
            className="w-full box-border border border-[#e0e0e0] rounded-lg px-3 py-2 text-[14px] text-[#1c1c1c] focus:outline-none focus:border-[#0084c7]"
          />
        </div>

        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="has_expiry"
            checked={hasExpiry}
            onChange={e => setHasExpiry(e.target.checked)}
            className="w-4 h-4 accent-[#0084c7]"
          />
          <label htmlFor="has_expiry" className="text-[13px] text-[#1c1c1c]">
            Dokumente dieser Kategorie haben ein Ablaufdatum
          </label>
        </div>

        {hasExpiry && (
          <div>
            <label className="block text-[12px] font-medium text-[#6b6b6b] mb-1">
              Standardgültigkeit (Monate)
            </label>
            <input
              type="number"
              min={1}
              value={validityMonths}
              onChange={e => setValidityMonths(e.target.value)}
              placeholder="z. B. 60"
              className="w-full box-border border border-[#e0e0e0] rounded-lg px-3 py-2 text-[14px] text-[#1c1c1c] focus:outline-none focus:border-[#0084c7]"
            />
          </div>
        )}

        {error && <p className="text-[12px] text-[#c62828]">{error}</p>}

        <div className="flex justify-end gap-3 mt-6">
          <Button variant="secondary" onClick={onClose}>Abbrechen</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={loading}>
            {loading ? 'Speichern…' : 'Speichern'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

interface CategoriesViewProps {
  categories: DocumentCategory[];
  onRefresh: () => void;
}

const CategoriesView = ({ categories, onRefresh }: CategoriesViewProps) => {
  const [modal, setModal] = useState<{ mode: 'create' | 'edit'; existing?: DocumentCategory } | null>(null);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<DocumentCategory | null>(null);
  const { mutate: deleteCategory } = useDeleteDocumentCategory();

  const handleDelete = (cat: DocumentCategory) => {
    setDeleteConfirm(cat);
    setOpenDropdownId(null);
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={() => setModal({ mode: 'create' })}
          className="flex items-center gap-2 px-4 py-2 text-[14px] font-medium bg-[#0084c7] text-white rounded-lg hover:bg-[#006fa8] transition-colors"
        >
          + Kategorie erstellen
        </button>
      </div>

      {categories.length === 0 ? (
        <div className="text-center py-16 text-[#9ca3af]">
          <p className="text-[#6b6b6b] text-[16px]">Keine Kategorien vorhanden</p>
        </div>
      ) : (
        <div className="overflow-visible">
          <div className="grid grid-cols-[1fr_120px_180px_50px] rounded-lg gap-4 px-4 py-3 bg-[#f5f5f5] text-[14px] font-medium text-[#6b6b6b]">
            <div>Name</div>
            <div>Ablaufdatum</div>
            <div>Gültigkeit</div>
            <div></div>
          </div>

          <div className="divide-y divide-[#f0f0f0]">
            {categories.map(cat => (
              <div
                key={cat.id}
                className="grid grid-cols-[1fr_120px_180px_50px] gap-4 px-4 py-3 items-center hover:bg-[#f9f9f9] transition-colors"
              >
                <div className="text-[16px] font-medium text-[#1c1c1c]">{cat.name}</div>
                <div>
                  {cat.has_expiry ? (
                    <span className="inline-flex px-4 py-2 rounded-full text-[16px] font-medium bg-[#fff8e1] text-[#f57f17]">Ja</span>
                  ) : (
                    <span className="inline-flex px-4 py-2 rounded-full text-[16px] font-medium bg-[#f5f5f5] text-[#6b6b6b]">Nein</span>
                  )}
                </div>
                <div className="text-[16px] text-[#6b6b6b]">
                  {cat.validity_months != null ? `${cat.validity_months} Monate` : '–'}
                </div>
                <div className="relative flex justify-end">
                  <button
                    onClick={() => setOpenDropdownId(openDropdownId === cat.id ? null : cat.id)}
                    className="p-1 rounded-[6px] hover:bg-[#f5f5f5] transition-colors"
                  >
                    <MoreVertical className="size-5 text-[#6b6b6b]" />
                  </button>
                  {openDropdownId === cat.id && (
                    <div className="absolute right-0 top-8 bg-white border border-[#e9e9e9] rounded-[6px] shadow-lg z-10 min-w-[160px]">
                      <button
                        onClick={() => { setModal({ mode: 'edit', existing: cat }); setOpenDropdownId(null); }}
                        className="w-full px-4 py-2.5 text-left text-[14px] hover:bg-[#f5f5f5]"
                      >
                        Bearbeiten
                      </button>
                      <button
                        onClick={() => { handleDelete(cat); setOpenDropdownId(null); }}
                        className="w-full px-4 py-2.5 text-left text-[14px] hover:bg-[#f5f5f5]"
                      >
                        Löschen
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {modal && (
        <CategoryModal
          mode={modal.mode}
          existing={modal.existing}
          onClose={() => setModal(null)}
          onSuccess={onRefresh}
        />
      )}

      {deleteConfirm && (
        <ConfirmModal
          isOpen={!!deleteConfirm}
          onClose={() => setDeleteConfirm(null)}
          title="Kategorie löschen"
          message={`Möchten Sie die Kategorie "${deleteConfirm.name}" wirklich löschen?`}
          onConfirm={() => {
            deleteCategory(deleteConfirm.id, { onSuccess: onRefresh });
            setDeleteConfirm(null);
          }}
        />
      )}
    </div>
  );
}

interface UploadModalProps {
  mode: 'create' | 'edit';
  users: User[];
  categories: DocumentCategory[];
  prefillCategoryId?: string;
  prefillUserId?: string;
  existingValidFrom?: string | null;
  existingValidUntil?: string | null;
  editDocumentId?: string;
  onClose: () => void;
  onSuccess: () => void;
}

const UploadModal = ({
  mode,
  users,
  categories,
  prefillCategoryId,
  prefillUserId,
  editDocumentId,
  existingValidFrom,
  existingValidUntil,
  onClose,
  onSuccess,
}: UploadModalProps) => {
  const [userId, setUserId] = useState(prefillUserId ?? '');
  const userOptions = useMemo(
    () => users.map(u => ({ value: u.id, label: u.full_name })),
    [users]
  );
  const [categoryId, setCategoryId] = useState(prefillCategoryId ?? '');
  const [file, setFile] = useState<File | null>(null);
  const [validFrom, setValidFrom] = useState(existingValidFrom ?? '');
  const [validUntil, setValidUntil] = useState(existingValidUntil ?? '');
  const [error, setError] = useState('');

  const { mutate: createDocument, isPending: creating } = useCreateDocument();
  const { mutate: updateDocument, isPending: updating } = useUpdateDocument();

  const loading = creating || updating;

  useEffect(() => {
    if (!categoryId) return;
    const cat = categories.find(c => c.id === categoryId);
    if (!cat?.validity_months) return;

    const from = new Date();
    const to = new Date();
    to.setMonth(to.getMonth() + cat.validity_months);
    to.setDate(to.getDate() - 1);

    const fmt = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };

    setValidFrom(fmt(from));
    setValidUntil(fmt(to));
  }, [categoryId, categories]);

  const handleSubmit = () => {
    setError('');
    if (validFrom && validUntil && validFrom > validUntil) {
      setError('Das Startdatum darf nicht nach dem Enddatum liegen.');
      return;
    }
    if ((validFrom && !validUntil) || (!validFrom && validUntil)) {
      setError('Bitte entweder beide Daten ausfüllen oder beide leer lassen.');
      return;
    }
    if (mode === 'create') {
      if (!userId || !categoryId) {
        setError('Bitte Mitarbeiter und Kategorie auswählen.');
        return;
      }
      createDocument(
        {
          user_id: userId,
          document_category_id: categoryId,
          file,
          valid_from: validFrom || null,
          valid_until: validUntil || null,
        },
        {
          onSuccess: () => { onSuccess(); onClose(); },
        onError: (e: any) => {
        const msg = e.message ?? '';
        if (msg.includes('must not be null') && msg.includes('file')) {
          setError('Bitte eine PDF-Datei auswählen.');
        } else {
          setError(msg || 'Fehler beim Speichern.');
        }
      },
        }
      );
    } else {
      updateDocument(
        {
          id: editDocumentId!,
          file,
          valid_from: validFrom || null,
          valid_until: validUntil || null,
        },
        {
          onSuccess: () => { onSuccess(); onClose(); },
          onError: (e: any) => setError(e.message ?? 'Fehler beim Speichern.'),
        }
      );
    }
  }

return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={mode === 'create' ? 'Dokument hochladen' : 'Dokument bearbeiten'}
    >
      <div className="space-y-4">
        {mode === 'create' && (
          <div>
            <label className="block text-[12px] font-medium text-[#6b6b6b] mb-1">Mitarbeiter *</label>
            <SearchableSelect
              value={userId || undefined}
              onChange={id => setUserId(id ?? '')}
              options={userOptions}
              placeholder="Auswählen"
              searchPlaceholder="Mitarbeiter suchen..."
              noResultsText="Keine Mitarbeiter gefunden"
              className="w-full"
            />
          </div>
        )}

        {mode === 'create' && (
          <div>
            <label className="block text-[12px] font-medium text-[#6b6b6b] mb-1">Kategorie *</label>
            <select
              value={categoryId}
              onChange={e => setCategoryId(e.target.value)}
              className="form-select px-4 py-2 border border-[#e0e0e0] rounded-lg text-[#1c1c1c] focus:outline-none focus:ring-2 focus:ring-[#0084c7] focus:border-transparent min-w-[200px] w-full"
            >
              <option value="">Auswählen</option>
              {categories.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="block text-[12px] font-medium text-[#6b6b6b] mb-1">
            PDF-Datei {mode === 'create' ? '*' : '(leer lassen = unverändert)'}
          </label>
          <input
            type="file"
            accept="application/pdf"
            onChange={e => setFile(e.target.files?.[0] ?? null)}
            className="w-full text-[13px] text-[#1c1c1c] file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-[12px] file:font-medium file:bg-[#e8f4fb] file:text-[#0084c7] hover:file:bg-[#d0eaf7] cursor-pointer"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[12px] font-medium text-[#6b6b6b] mb-1">Gültig ab</label>
            <input
              type="date"
              value={validFrom}
              onChange={e => setValidFrom(e.target.value)}
              className="w-full border box-border border-[#e0e0e0] rounded-lg px-3 py-2 text-[13px] text-[#1c1c1c] focus:outline-none focus:border-[#0084c7]"
            />
          </div>
          <div>
            <label className="block text-[12px] font-medium text-[#6b6b6b] mb-1">Gültig bis</label>
            <input
              type="date"
              value={validUntil}
              onChange={e => setValidUntil(e.target.value)}
              className="w-full border box-border border-[#e0e0e0] rounded-lg px-3 py-2 text-[13px] text-[#1c1c1c] focus:outline-none focus:border-[#0084c7]"
            />
          </div>
        </div>

        {error && <p className="text-[12px] text-red-500">{error}</p>}

        <div className="flex justify-end gap-3 mt-6">
          <Button variant="secondary" onClick={onClose}>Abbrechen</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={loading}>
            {loading ? 'Speichern…' : 'Speichern'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

interface HistoryPanelProps {
  category: DocumentCategory;
  documents: Document[];
  onEdit: (doc: Document) => void;
  onDelete: (id: string) => void;
  onAddVersion: () => void;
}

const HistoryPanel = ({ category, documents, onEdit, onDelete }: HistoryPanelProps) => {
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const { token } = useAuth();

  const latest = documents[0];
  const older = documents.slice(1);

  const downloadDoc = async (docId: string) => {
    try {
      const response = await fetch(
        `${DEFAULT_ENDPOINT_URL_EXPORT}/documents/${docId}/download?t=${Date.now()}`,
        { method: 'GET', headers: { Authorization: token, 'Cache-Control': 'no-cache' } }
      );
      if (!response.ok) {
        toast.error('Dokument konnte nicht geöffnet werden.');
        return;
      }
      const blob = await response.blob();
      const pdfUrl = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      window.open(pdfUrl, '_blank');
      window.URL.revokeObjectURL(pdfUrl);
    } catch (err) {
      console.error(err);
    }
  };

  const renderRow = (doc: Document, index: number) => (
    <div
      key={doc.id}
      className="grid grid-cols-[1fr_160px_180px_180px_50px] gap-4 px-4 py-3 items-center hover:bg-[#f9f9f9] transition-colors bg-white"
    >
      <div className="flex items-center gap-2">
        <span className="text-[16px] text-[#1c1c1c]">
          {index === 0 ? 'Aktuelle Version' : `Version ${documents.length - index}`}
        </span>
      </div>
      <div><StatusBadge valid_until={doc.valid_until} /></div>
      <div className="text-[16px] text-[#6b6b6b]">{formatDate(doc.created_at)}</div>
      <div className="text-[16px] text-[#6b6b6b]">
        {doc.valid_from || doc.valid_until
          ? `${formatDate(doc.valid_from)} – ${formatDate(doc.valid_until)}`
          : '–'}
      </div>
      <div className="relative flex justify-end">
        <button
          onClick={() => setOpenDropdownId(openDropdownId === doc.id ? null : doc.id)}
          className="p-1 rounded-[6px] hover:bg-[#f5f5f5] transition-colors"
        >
          <MoreVertical className="size-5 text-[#6b6b6b]" />
        </button>
        {openDropdownId === doc.id && (
          <div className="absolute right-0 top-8 bg-white border border-[#e9e9e9] rounded-[6px] shadow-lg z-10 min-w-[160px]">
            <button
              onClick={async () => { setOpenDropdownId(null); await downloadDoc(doc.id); }}
              className="w-full px-4 py-2.5 text-left text-[14px] hover:bg-[#f5f5f5]"
            >
              Herunterladen
            </button>
            {index === 0 && (
              <button
                onClick={() => { onEdit(doc); setOpenDropdownId(null); }}
                className="w-full px-4 py-2.5 text-left text-[14px] hover:bg-[#f5f5f5]"
              >
                Bearbeiten
              </button>
            )}
            <button
              onClick={() => { onDelete(doc.id); setOpenDropdownId(null); }}
              className="w-full px-4 py-2.5 text-left text-[14px] text-[#c62828] hover:bg-[#f5f5f5]"
            >
              Löschen
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="overflow-visible">
      <div className="grid grid-cols-[1fr_160px_180px_180px_50px] gap-4 px-4 py-3 bg-[#f9f9f9] rounded-lg text-[14px] font-medium text-[#6b6b6b] mt-4 first:mt-0">
        <div className="flex items-center gap-2 text-[#1c1c1c] font-semibold">
          {category.name}
          <span className="text-[11px] font-normal text-[#9ca3af]">
            {documents.length} Version{documents.length !== 1 ? 'en' : ''}
          </span>
        </div>
        <div>Status</div>
        <div>Hochgeladen</div>
        <div>Zeitraum</div>
        <div></div>
      </div>

      <div className="divide-y divide-[#f0f0f0]">
        {latest && renderRow(latest, 0)}
      </div>

      {older.length > 0 && (
        <>
          <button
            onClick={() => setHistoryOpen(p => !p)}
            className="w-full flex items-center gap-2 px-4 py-2 text-[13px] text-[#6b6b6b] hover:text-[#1c1c1c] hover:bg-[#f5f5f5] transition-colors border-t border-[#f0f0f0]"
          >
            <ChevronRight className={`size-4 transition-transform ${historyOpen ? 'rotate-90' : ''}`} />
            {historyOpen ? '' : `${older.length} ältere Version${older.length !== 1 ? 'en' : ''} anzeigen`}
          </button>
          {historyOpen && (
            <div className="divide-y divide-[#f0f0f0]">
              {older.map((doc, i) => renderRow(doc, i + 1))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

interface DocumentsViewProps {
  users: User[];
}

export const DocumentsView = ({ users }: DocumentsViewProps) => {
  const [activeView, setActiveView] = useState<ActiveView>('documents');
  const [selectedUserId, setSelectedUserId] = useState<string>(users[0]?.id ?? '');
  const userOptions = useMemo(
    () => users.map(u => ({ value: u.id, label: u.full_name })),
    [users]
  );
  const [deleteDocConfirm, setDeleteDocConfirm] = useState<string | null>(null);
  const [uploadModal, setUploadModal] = useState<{
    mode: 'create' | 'edit';
    prefillCategoryId?: string;
    editDocumentId?: string;
    existingValidFrom?: string | null;
    existingValidUntil?: string | null;
  } | null>(null);

  const { data: categories = [], refetch: refetchCategories } = useDocumentCategories();
  const { data: documentsSorted = [], refetch: refetchDocuments } = useDocumentsForUser(selectedUserId);
  const { mutate: deleteDocument } = useDeleteDocument();

  const handleDeleteDocument = (id: string) => {
    setDeleteDocConfirm(id);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between border-b border-[#e0e0e0] pb-2">
        <div className="flex">
          <button
            onClick={() => setActiveView('documents')}
            className={`px-4 py-2 text-[16px] transition-colors relative ${
              activeView === 'documents'
                ? 'text-[#0084c7] font-medium'
                : 'text-[#6b6b6b] hover:text-[#1c1c1c]'
            }`}
          >
            Dokumente
            {activeView === 'documents' && (
              <div className="absolute bottom-[-10px] left-0 right-0 h-[2px] bg-[#0084c7]" />
            )}
          </button>
          <button
            onClick={() => setActiveView('categories')}
            className={`px-4 py-2 text-[16px] transition-colors relative ${
              activeView === 'categories'
                ? 'text-[#0084c7] font-medium'
                : 'text-[#6b6b6b] hover:text-[#1c1c1c]'
            }`}
          >
            Kategorien
            {activeView === 'categories' && (
              <div className="absolute bottom-[-10px] left-0 right-0 h-[2px] bg-[#0084c7]" />
            )}
          </button>
        </div>
      </div>

      {activeView === 'categories' && (
        <CategoriesView
          categories={categories}
          onRefresh={refetchCategories}
        />
      )}

      {activeView === 'documents' && (
        <>
          <div className="flex items-center justify-between">
            <SearchableSelect
              value={selectedUserId || undefined}
              onChange={id => id && setSelectedUserId(id)}
              options={userOptions}
              placeholder="Mitarbeiter auswählen"
              searchPlaceholder="Mitarbeiter suchen..."
              noResultsText="Keine Mitarbeiter gefunden"
            />
            <button
              onClick={() => setUploadModal({ mode: 'create' })}
              className="flex items-center gap-2 px-4 py-2 text-[14px] font-medium bg-[#0084c7] text-white rounded-lg hover:bg-[#006fa8] transition-colors"
            >
              + Dokument hochladen
            </button>
          </div>

          {documentsSorted.length === 0 ? (
            <div className="text-center py-16 text-[#9ca3af]">
              <p className="text-[#6b6b6b] text-[16px]">Keine Dokumente vorhanden</p>
            </div>
          ) : (
            <div className="space-y-3">
              {documentsSorted.map(({ documentCategory, documents }) => (
                <HistoryPanel
                  key={documentCategory.id}
                  category={documentCategory}
                  documents={documents}
                  onAddVersion={() =>
                    setUploadModal({ mode: 'create', prefillCategoryId: documentCategory.id })
                  }
                  onEdit={doc => setUploadModal({ mode: 'edit', editDocumentId: doc.id, existingValidFrom: doc.valid_from, existingValidUntil: doc.valid_until })}
                  onDelete={handleDeleteDocument}
                />
              ))}
            </div>
          )}
        </>
      )}

      {uploadModal && (
        <UploadModal
          mode={uploadModal.mode}
          users={users}
          categories={categories}
          prefillCategoryId={uploadModal.prefillCategoryId}
          prefillUserId={selectedUserId}
          editDocumentId={uploadModal.editDocumentId}
          existingValidFrom={uploadModal.existingValidFrom}
          existingValidUntil={uploadModal.existingValidUntil}
          onClose={() => setUploadModal(null)}
          onSuccess={() => refetchDocuments()}
        />
      )}
      {deleteDocConfirm && (
        <ConfirmModal
          isOpen={!!deleteDocConfirm}
          onClose={() => setDeleteDocConfirm(null)}
          title="Dokument löschen"
          message="Möchten Sie dieses Dokument wirklich löschen?"
          onConfirm={() => {
            deleteDocument(deleteDocConfirm, { onSuccess: () => refetchDocuments() });
            setDeleteDocConfirm(null);
          }}
        />
      )}
    </div>
  );
}